/**
 * Resume Simulation dialog.
 *
 * Rendered on the {@link ExamSimulation} page when a recoverable draft is
 * detected for the signed-in user. Offers two choices:
 *   - "Resume" — rehydrate selection/session/step and jump straight back into
 *     the module the learner was on.
 *   - "Start fresh" — discard all drafts for this user and clear any
 *     persisted store state, so the flow starts at `exam-type`.
 *
 * Deliberately uses the existing AlertDialog primitive so visual language
 * stays consistent with the rest of the app.
 *
 * @module ResumeSimulationDialog
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { DraftIndexEntry } from '@/lib/simulationDraftStorage';

interface ResumeSimulationDialogProps {
  /** Whether the dialog is open. */
  readonly open: boolean;
  /** Draft being offered for resume (newest entry for this user/session). */
  readonly draft: DraftIndexEntry | null;
  /** User confirms resuming. */
  readonly onResume: () => void;
  /** User chooses to discard and start fresh. */
  readonly onDiscard: () => void;
}

function formatRelativeTime(updatedAt: number, locale: string): string {
  const diffMs = Date.now() - updatedAt;
  const diffMinutes = Math.max(1, Math.round(diffMs / 60_000));
  if (diffMinutes < 60) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
      -diffMinutes,
      'minute',
    );
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-diffHours, 'hour');
  }
  const diffDays = Math.round(diffHours / 24);
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-diffDays, 'day');
}

export function ResumeSimulationDialog({
  open,
  draft,
  onResume,
  onDiscard,
}: ResumeSimulationDialogProps) {
  const { t, i18n } = useTranslation();

  const description = useMemo(() => {
    if (!draft) return '';
    const moduleLabel = t(`exam.modules.${draft.module}`, { defaultValue: draft.module });
    const relative = formatRelativeTime(draft.updatedAt, i18n.language || 'en');
    return t('simulation.recovery.description', {
      defaultValue: 'Pick up where you left off in the {{module}} module ({{when}}).',
      module: moduleLabel,
      when: relative,
    });
  }, [draft, i18n.language, t]);

  if (!draft) {
    return null;
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('simulation.recovery.title', { defaultValue: 'Resume simulation?' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onDiscard}>
            {t('simulation.recovery.startFresh', { defaultValue: 'Start fresh' })}
          </AlertDialogCancel>
          <AlertDialogAction onClick={onResume}>
            {t('simulation.recovery.resume', { defaultValue: 'Resume' })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
